import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncIcon from "@mui/icons-material/Sync";
import { CircularProgress } from "@mui/material";
import Button from "@components/Button/Button";
import ConfirmUnclaimDialog from "@components/ConfirmUnclaimDialog/ConfirmUnclaimDialog";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import Subheader from "@components/Subheader/Subheader";
import type {
  DateRangeValue,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { useNotification } from "@context/NotificationContext";
import type { ValidityPeriodType } from "@features/port-security/components/SecurityClearence.types.ts";
import {
  getBulkValidityPeriodsOptions,
  getClearancesFilterFields,
  getSecurityClearanceBadgeType,
  SECURITY_CLEARANCE_STATUS,
  validityPeriodIntToString,
} from "@features/port-security/components/utils.ts";
import { useFormattedReference } from "@hooks/useFormattedReference.ts";
import { useRestrictedNationalities } from "@hooks/useRestrictedNationalities";
import PageLayout from "@layouts/PageLayout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { Nationality } from "@services/gatepass/types";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi.ts";
import {
  bulkCancelSecurityClearance,
  bulkCompleteSecurityClearance,
  bulkUnsuspendSecurityClearance,
  bulkUpdateSecurityClearanceTasks,
  getSecurityClearances,
  getSecurityClearancesQueryKey,
  SECURITY_CLEARANCE_BULK_MAX,
} from "@services/securityClearanceApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { runTransaction } from "@telemetry/runTransaction";
import { completeScreenNavigation } from "@telemetry/trackNavigation";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { toYesNo } from "@utils/formatters/toYesNo";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import BulkApproveSecurityClearanceDrawer from "./form/BulkApproveSecurityClearanceDrawer";
import BulkReasonSecurityClearanceDrawer from "./form/BulkReasonSecurityClearanceDrawer";
import { notifyBulkTaskOutcome } from "./form/bulkSecurityClearanceDrawerShared";

const LOCAL_STORAGE_PAGE_KEY = "security-clearance-page";
const LOCAL_STORAGE_PAGE_SIZE_KEY = "security-clearance-page-size";
/** List search can lag behind bulk mutations; retry once after a short delay. */
const SECURITY_CLEARANCES_LIST_REFRESH_RETRY_MS = 1500;
const SEARCH_INQUIRY_ID = "port-security.security-clearances.search";
const SEARCH_INQUIRY_CONTEXT = {
  module: "security_clearance",
  screen: "Security Clearances",
  action: "search_security_clearances",
  entityType: "security_clearance",
};
const BULK_SECURITY_CLEARANCE_TRANSACTION_CONTEXT = {
  module: "security_clearance",
  screen: "Security Clearances",
  entityType: "security_clearance",
};

export const DATE_TIME_WITHOUT_TIMEZONE_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}$/;

export const toIsoDateOrNull = (
  value: string | DateRangeValue,
  options?: { treatDateTimeWithoutTimezoneAsUtc?: boolean },
) => {
  const raw = typeof value === "string" ? value : (value?.from ?? "");
  if (!raw) return null;
  const shouldTreatAsUtc =
    options?.treatDateTimeWithoutTimezoneAsUtc && DATE_TIME_WITHOUT_TIMEZONE_REGEX.test(raw);
  const d = new Date(shouldTreatAsUtc ? `${raw}Z` : raw);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

export const buildFilterBodyFromUI = (filters: FilterItem[]) => {
  const body: Record<string, any> = {};

  for (const f of filters) {
    if (!f.field || f.value === "" || f.value == null) continue;

    switch (f.field) {
      case "passportNumber":
        body.passportNumbers = [...(body.passportNumbers ?? []), f.value];
        break;
      case "emiratesIdNumber":
        body.emiratesIdNumbers = [...(body.emiratesIdNumbers ?? []), f.value];
        break;
      case "nationality":
        body.nationalities = [...(body.nationalities ?? []), f.value];
        break;
      case "name":
        body.names = [...(body.names ?? []), f.value];
        break;
      case "clearanceStatus":
        body.clearanceStatuses = [...(body.clearanceStatuses ?? []), f.value];
        break;

      case "validityDate":
        body.validityPeriods = [...(body.validityPeriods ?? []), f.value];
        break;

      case "dateSubmitted": {
        const valueAsRange = f.value as { from?: string; to?: string };
        body[`minimumCreatedAt`] = toIsoDateOrNull(valueAsRange.from ?? "");
        body[`maximumCreatedAt`] = toIsoDateOrNull(valueAsRange.to ?? "", {
          treatDateTimeWithoutTimezoneAsUtc: true,
        });
        break;
      }

      default:
        break;
    }
  }

  return body;
};

// Generate filters based on user role
// Security Operator: Filter for "PENDING_SECURITY_DEPT_APPROVAL"
// Security Supervisor: Filter for both "PENDING_SECURITY_DEPT_APPROVAL" and "PENDING_SECURITY_SUPERVISOR_APPROVAL"
const getDefaultFilters = (
  isSecurityOperator: boolean,
  isSecuritySupervisor: boolean,
): FilterItem[] => {
  const filters: FilterItem[] = [];

  if (isSecurityOperator) {
    filters.push({
      field: "clearanceStatus",
      value: "PENDING_SECURITY_DEPT_APPROVAL",
    });
  } else if (isSecuritySupervisor) {
    filters.push({
      field: "clearanceStatus",
      value: "PENDING_SECURITY_DEPT_APPROVAL",
    });
    filters.push({
      field: "clearanceStatus",
      value: "PENDING_SECURITY_SUPERVISOR_APPROVAL",
    });
  }

  return filters;
};

const ClearancesContainer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useNotification();
  const { user, requestSCLabelKey, userActionPermissions } = useAuthStore();

  const currentUsername = user?.preferredUsername ?? "";
  const userRoles = user?.roles ?? [];
  const isSecurityOperator = userRoles.includes("security-operator");
  const isSecuritySupervisor = userRoles.includes("security-supervisor");
  const canUseBulkActions =
    checkPermission(userActionPermissions, "hasSecurityClearanceCheckbox") &&
    (isSecurityOperator || isSecuritySupervisor);
  // Operators verify directly. Supervisors verify directly at the security-dept stage (POFDV001-8088).
  const canDirectApprove = checkPermission(
    userActionPermissions,
    "canDirectlyApproveSecurityClearance",
  );
  const canReject = checkPermission(userActionPermissions, "canRejectSecurityClearance");
  const canSuspend = checkPermission(userActionPermissions, "canSuspendSecurityClearance");
  const canUnsuspend = checkPermission(userActionPermissions, "canUnsuspendSecurityClearance");
  const canCancel = checkPermission(userActionPermissions, "canCancelSecurityClearance");

  const { options: validityPeriods } = useFormattedReference<ValidityPeriodType>(
    REFERENCE_DATA_TYPES.SECURITY_CLEARANCE_VALIDITY_PERIODS,
    "bff-gatepass",
  );
  const { options: nationalities } = useFormattedReference<Nationality>(
    REFERENCE_DATA_TYPES.NATIONALITY,
    "bff-gatepass",
  );
  const { options: restrictedNationalities } = useRestrictedNationalities();

  const validityPeriodsOptions = validityPeriods.map((validityPeriod: ValidityPeriodType) => ({
    label: validityPeriod[Object.keys(validityPeriod)[0]],
    value: validityPeriod[Object.keys(validityPeriod)[0]],
  }));

  const nationalitiesOptions = useMemo(
    () =>
      nationalities
        .map((n) => ({ label: n.nationality, value: n.nationality }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [nationalities],
  );

  const availableFields = useMemo(
    () => getClearancesFilterFields(t, nationalitiesOptions, validityPeriodsOptions),
    [t, nationalitiesOptions, validityPeriodsOptions],
  );

  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_KEY);
    return saved ? parseInt(saved, 10) : 1;
  });

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_SIZE_KEY);
    return saved ? parseInt(saved, 10) : 25;
  });

  const [filters, setFilters] = useState<FilterItem[]>(() => {
    const saved = localStorage.getItem("security-clearances-filters");
    const defaultFiltersApplied = localStorage.getItem("security-clearances-default-filters");
    if (saved || defaultFiltersApplied) {
      return JSON.parse(saved ?? "[]");
    }
    // Apply default filters based on user role if no saved filters exist
    const filters = getDefaultFilters(isSecurityOperator, isSecuritySupervisor);
    localStorage.setItem("security-clearances-filters", JSON.stringify(filters));
    localStorage.setItem("security-clearances-default-filters", String(true));
    return filters;
  });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const onApplyFilters = (f: FilterItem[]) => {
    startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
    setFilters(f);
    localStorage.setItem("security-clearances-filters", JSON.stringify(f));
    setPage(1);
    localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, "1");
  };

  const onClearFilters = () => {
    if (filters.length) {
      startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
    }
    setFilters([]);
    localStorage.removeItem("security-clearances-filters");
  };

  const securityClearancesQueryKey = useMemo(
    () => getSecurityClearancesQueryKey(page, pageSize, filters),
    [page, pageSize, filters],
  );

  const refreshClearancesList = useCallback(async () => {
    await queryClient.refetchQueries({
      queryKey: securityClearancesQueryKey,
      exact: true,
    });
  }, [queryClient, securityClearancesQueryKey]);

  /** Refetch immediately and once more after a short delay (list index eventual consistency). */
  const refreshClearancesListAfterMutation = useCallback(() => {
    void refreshClearancesList();
    window.setTimeout(() => {
      void refreshClearancesList();
    }, SECURITY_CLEARANCES_LIST_REFRESH_RETRY_MS);
  }, [refreshClearancesList]);

  const { data, isFetching, isLoading, isError, isSuccess, dataUpdatedAt, error } = useQuery({
    queryKey: securityClearancesQueryKey,
    queryFn: () => {
      const filterBody = buildFilterBodyFromUI(filters);
      return getSecurityClearances({
        service: "bff-gatepass",
        page: page - 1,
        size: pageSize,
        filters: filterBody,
      });
    },
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    structuralSharing: false,
  });
  useTrackPendingInquiry(SEARCH_INQUIRY_ID, {
    isFetching,
    isLoading,
    isError,
    isSuccess,
    error,
    dataUpdatedAt,
  });

  const startedAt = useRef(performance.now());
  const hasTrackedInitialLoad = useRef(false);

  useEffect(() => {
    if (isLoading || error || hasTrackedInitialLoad.current) {
      return;
    }

    hasTrackedInitialLoad.current = true;
    completeScreenNavigation("Security Clearance");
    trackScreenLoad("Information Services", "Security Clearance", startedAt.current);
  }, [error, isLoading]);

  const mapStatus = (status: string): string =>
    t(`security-clearance.status-labels.${status}`, {
      defaultValue: t("common.unknown"),
    });

  const clearances = data?.securityClearances ?? [];
  const totalCount = data?.totalRecordCount ?? 0;

  const headers: Header[] = [
    { field: "serialNo", columnName: t("security-clearance.tableHeaders.serialNo") },
    { field: "passportNo", columnName: t("security-clearance.tableHeaders.passportNo") },
    { field: "emiratesIdNumber", columnName: t("security-clearance.tableHeaders.emiratesId") },
    { field: "nationality", columnName: t("security-clearance.tableHeaders.nationality") },
    { field: "name", columnName: t("security-clearance.tableHeaders.name") },
    { field: "arabicName", columnName: t("security-clearance.tableHeaders.arabicName") },
    { field: "companyCode", columnName: t("security-clearance.tableHeaders.companyCode") },
    { field: "fottEntry", columnName: t("security-clearance.tableHeaders.fottEntry") },
    {
      field: "dateSubmitted",
      columnName: t("security-clearance.tableHeaders.dateSubmitted"),
      type: "date",
    },
    {
      field: "validityPeriod",
      columnName: t("security-clearance.tableHeaders.validityPeriod"),
    },
    { field: "status", columnName: t("security-clearance.tableHeaders.status") },
  ];

  const formattedFormData = useMemo(
    () =>
      clearances.map((row) => ({
        id: row.id,
        serialNo: row.serialNo,
        passportNo: row.passportNo ?? "N/A",
        emiratesIdNumber: row.emiratesIdNumber ?? "N/A",
        nationality: row.nationality,
        name: row.name,
        arabicName: row.arabicName,
        companyCode: row.companyCode,
        fottEntry: toYesNo(row.fottEntry, t),
        dateSubmitted: row.dateSubmitted,
        validityPeriod: validityPeriodIntToString(Number(row.validityPeriod), t),
        status: mapStatus(row.status ?? "NULL"),
        statusCode: row.status ?? "",
        // Keep CLOSED badge color as success; label is Cancelled in the UI
        badgeType: getSecurityClearanceBadgeType(row.status),
        taskId: row.taskId ?? null,
        taskStatus: row.taskStatus ?? null,
        claimOwnerName: row.claimOwnerName ?? null,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearances, t],
  );

  type ClearanceRow = (typeof formattedFormData)[number];

  const [bulkApproveDrawerOpen, setBulkApproveDrawerOpen] = useState(false);
  const [bulkResubmitDrawerOpen, setBulkResubmitDrawerOpen] = useState(false);
  const [bulkRejectDrawerOpen, setBulkRejectDrawerOpen] = useState(false);
  const [bulkSuspendDrawerOpen, setBulkSuspendDrawerOpen] = useState(false);
  const [unsuspendDialogOpen, setUnsuspendDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [unclaimDialogOpen, setUnclaimDialogOpen] = useState(false);

  const selectedRows = useMemo(
    () => formattedFormData.filter((r) => selectedIds.includes(r.id)),
    [formattedFormData, selectedIds],
  );

  const bulkValidityPeriodOptions = useMemo(
    () =>
      getBulkValidityPeriodsOptions(
        validityPeriods,
        selectedRows.map((row) => row.nationality),
        restrictedNationalities,
      ),
    [restrictedNationalities, validityPeriods, selectedRows],
  );

  const isRowSelectable = useCallback(
    (row: ClearanceRow) => {
      if (!canUseBulkActions) return false;

      const statusCode = row.statusCode;
      const claimOwnerName = row.claimOwnerName ?? null;

      // Approved requests can be suspended and/or cancelled (id-based, no task required).
      if (statusCode === SECURITY_CLEARANCE_STATUS.APPROVED) {
        return canSuspend || canCancel;
      }

      // Suspended requests can be unsuspended (id-based, no task required).
      if (statusCode === SECURITY_CLEARANCE_STATUS.SUSPENDED) {
        return canUnsuspend;
      }

      // Pending approval stages (claim / unclaim / approve / verify / resubmit / permanently reject)
      // operate on the workflow task, so a task id is required.
      if (!row.taskId) return false;

      // Operators act on the security-dept stage. Supervisors act on BOTH the security-dept
      // (verify) and supervisor-approval stages (POFDV001-8088).
      const isDeptStage = statusCode === SECURITY_CLEARANCE_STATUS.PENDING_SECURITY_DEPT_APPROVAL;
      const isSupervisorStage =
        statusCode === SECURITY_CLEARANCE_STATUS.PENDING_SECURITY_SUPERVISOR_APPROVAL;

      const operatorActionable = isSecurityOperator && isDeptStage;
      const supervisorActionable = isSecuritySupervisor && (isDeptStage || isSupervisorStage);

      if (!operatorActionable && !supervisorActionable) return false;

      if (!claimOwnerName) {
        return row.taskStatus === "READY";
      }

      return claimOwnerName === currentUsername;
    },
    [
      canUseBulkActions,
      canSuspend,
      canCancel,
      canUnsuspend,
      isSecurityOperator,
      isSecuritySupervisor,
      currentUsername,
    ],
  );

  useEffect(() => {
    if (!selectedIds.length) return;

    // Drop IDs that left the page or are no longer bulk-actionable
    const validIds = selectedIds.filter((id) => {
      const row = formattedFormData.find((r) => r.id === id);
      return Boolean(row && isRowSelectable(row));
    });

    if (validIds.length !== selectedIds.length) {
      setSelectedIds(validIds);
    }
  }, [formattedFormData, selectedIds, isRowSelectable]);

  const selectedAllDeptStage =
    selectedRows.length > 0 &&
    selectedRows.every(
      (row) => row.statusCode === SECURITY_CLEARANCE_STATUS.PENDING_SECURITY_DEPT_APPROVAL,
    );
  const selectedAllSupervisorStage =
    selectedRows.length > 0 &&
    selectedRows.every(
      (row) => row.statusCode === SECURITY_CLEARANCE_STATUS.PENDING_SECURITY_SUPERVISOR_APPROVAL,
    );
  const selectedAllPending = selectedAllDeptStage || selectedAllSupervisorStage;
  const selectedAllApproved =
    selectedRows.length > 0 &&
    selectedRows.every((row) => row.statusCode === SECURITY_CLEARANCE_STATUS.APPROVED);
  const selectedAllSuspended =
    selectedRows.length > 0 &&
    selectedRows.every((row) => row.statusCode === SECURITY_CLEARANCE_STATUS.SUSPENDED);

  const selectedOnlyUnclaimed =
    selectedAllPending && selectedRows.every((row) => !row.claimOwnerName);
  const selectedOnlyClaimedByCurrentUser =
    selectedAllPending &&
    selectedRows.every((row) => row.claimOwnerName && row.claimOwnerName === currentUsername);

  // Direct verify (no validity period, "Verify" copy) applies to operators, and to supervisors
  // acting at the security-dept stage. Supervisors at their own approval stage approve with a
  // validity period via the drawer (POFDV001-8088).
  const shouldDirectVerify = canDirectApprove || (isSecuritySupervisor && selectedAllDeptStage);
  const requireValidityPeriod = isSecuritySupervisor && selectedAllSupervisorStage;
  // Permanently reject is only available at the supervisor approval stage.
  const canBulkPermanentlyReject = canReject && selectedAllSupervisorStage;

  const handleSelectionChange = useCallback((rows: Array<{ id: string }>) => {
    setSelectedIds(rows.map((r) => r.id));
  }, []);

  const rowSelectionModel = useMemo(
    () => ({ type: "include" as const, ids: new Set(selectedIds) }),
    [selectedIds],
  );

  const clearSelectionAfterBulkComplete = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const getSelectedTaskIds = useCallback(
    () => selectedRows.map((r) => r.taskId).filter((taskId): taskId is string => Boolean(taskId)),
    [selectedRows],
  );

  const getSelectedClearanceIds = useCallback(() => selectedRows.map((r) => r.id), [selectedRows]);

  const notifyBulkLimitExceeded = useCallback(() => {
    notify({
      title: t("security-clearance.bulk-limit-title", { defaultValue: "Too many items selected" }),
      description: t("security-clearance.bulk-limit-detail", {
        defaultValue: "You can process up to {{max}} requests at once.",
        max: SECURITY_CLEARANCE_BULK_MAX,
      }),
      type: "warning",
    });
  }, [notify, t]);

  const notifyBulkMutationResult = useCallback(
    (
      result: { successfulCount?: number; failedCount?: number } | undefined,
      messages: {
        partialTitleKey: string;
        partialTitleDefault: string;
        partialDetailKey: string;
        partialDetailDefault: string;
        successTitleKey: string;
        successTitleDefault: string;
      },
    ) => {
      notifyBulkTaskOutcome(notify, t, result, messages);
    },
    [notify, t],
  );

  const exceedsBulkLimit = selectedRows.length > SECURITY_CLEARANCE_BULK_MAX;

  const bulkClaimMutation = useMutation({
    mutationFn: (taskIds: string[]) =>
      runTransaction(
        {
          ...BULK_SECURITY_CLEARANCE_TRANSACTION_CONTEXT,
          action: "bulk_claim_security_clearance_tasks",
        },
        (transactionId) =>
          bulkUpdateSecurityClearanceTasks({
            service: "bff-gatepass",
            taskIds,
            taskOperation: "CLAIM",
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: async (result) => {
      refreshClearancesListAfterMutation();
      notifyBulkMutationResult(result, {
        partialTitleKey: "security-clearance.bulk-claim-partial",
        partialTitleDefault: "Bulk claim completed with errors",
        partialDetailKey: "security-clearance.bulk-claim-partial-detail",
        partialDetailDefault: "{{success}} claimed, {{failed}} failed.",
        successTitleKey: "security-clearance.security-clearance-request-claimed",
        successTitleDefault:
          "You’ve claimed this task. Your claim remains active for 30 minutes of task activity.",
      });
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      notify({
        title: t("common.error"),
        description:
          error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const bulkUnclaimMutation = useMutation({
    mutationFn: (taskIds: string[]) =>
      runTransaction(
        {
          ...BULK_SECURITY_CLEARANCE_TRANSACTION_CONTEXT,
          action: "bulk_unclaim_security_clearance_tasks",
        },
        (transactionId) =>
          bulkUpdateSecurityClearanceTasks({
            service: "bff-gatepass",
            taskIds,
            taskOperation: "UNCLAIM",
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: async (result) => {
      refreshClearancesListAfterMutation();
      notifyBulkMutationResult(result, {
        partialTitleKey: "security-clearance.bulk-unclaim-partial",
        partialTitleDefault: "Bulk unclaim completed with errors",
        partialDetailKey: "security-clearance.bulk-unclaim-partial-detail",
        partialDetailDefault: "{{success}} unclaimed, {{failed}} failed.",
        successTitleKey: "security-clearance.security-clearance-request-unclaimed",
        successTitleDefault: "Task unclaimed. Other users can now claim it.",
      });
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      notify({
        title: t("common.error"),
        description:
          error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
    onSettled: () => {
      setUnclaimDialogOpen(false);
    },
  });

  const bulkUnsuspendMutation = useMutation({
    mutationFn: (securityClearanceIds: string[]) =>
      runTransaction(
        {
          ...BULK_SECURITY_CLEARANCE_TRANSACTION_CONTEXT,
          action: "bulk_unsuspend_security_clearances",
        },
        (transactionId) =>
          bulkUnsuspendSecurityClearance(
            "bff-gatepass",
            { securityClearanceIds },
            ...(transactionId ? [transactionId] : []),
          ),
      ),
    onSuccess: async (result) => {
      refreshClearancesListAfterMutation();
      setUnsuspendDialogOpen(false);
      clearSelectionAfterBulkComplete();
      notifyBulkMutationResult(result, {
        partialTitleKey: "security-clearance.bulk-unsuspend-partial",
        partialTitleDefault: "Bulk unsuspend completed with errors",
        partialDetailKey: "security-clearance.bulk-unsuspend-partial-detail",
        partialDetailDefault: "{{success}} unsuspended, {{failed}} failed.",
        successTitleKey: "security-clearance.security-clearance-request-unsuspended",
        successTitleDefault: "Security Clearance has been Unsuspended!",
      });
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      setUnsuspendDialogOpen(false);
      notify({
        title: t("common.error"),
        description:
          error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const bulkCancelMutation = useMutation({
    mutationFn: (securityClearanceIds: string[]) =>
      runTransaction(
        {
          ...BULK_SECURITY_CLEARANCE_TRANSACTION_CONTEXT,
          action: "bulk_cancel_security_clearances",
        },
        (transactionId) =>
          bulkCancelSecurityClearance(
            "bff-gatepass",
            { securityClearanceIds },
            ...(transactionId ? [transactionId] : []),
          ),
      ),
    onSuccess: async (result) => {
      refreshClearancesListAfterMutation();
      setCancelDialogOpen(false);
      clearSelectionAfterBulkComplete();
      notifyBulkMutationResult(result, {
        partialTitleKey: "security-clearance.bulk-cancel-partial",
        partialTitleDefault: "Bulk cancel completed with errors",
        partialDetailKey: "security-clearance.bulk-cancel-partial-detail",
        partialDetailDefault: "{{success}} canceled, {{failed}} failed.",
        successTitleKey: "security-clearance.security-clearance-request-cancelled",
        successTitleDefault: "Security Clearance has been Canceled!",
      });
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      setCancelDialogOpen(false);
      notify({
        title: t("common.error"),
        description:
          error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const bulkVerifyMutation = useMutation({
    mutationFn: (securityClearanceIds: string[]) =>
      bulkCompleteSecurityClearance("bff-gatepass", {
        items: securityClearanceIds.map((id) => ({ securityClearanceId: id })),
        taskDecision: { status: "APPROVED" },
      }),
    onSuccess: async (result) => {
      refreshClearancesListAfterMutation();
      setVerifyDialogOpen(false);
      clearSelectionAfterBulkComplete();
      notifyBulkMutationResult(result, {
        partialTitleKey: "security-clearance.bulk-verify-partial",
        partialTitleDefault: "Bulk verify completed with errors",
        partialDetailKey: "security-clearance.bulk-verify-partial-detail",
        partialDetailDefault: "{{success}} verified, {{failed}} failed.",
        successTitleKey: "security-clearance.security-clearance-request-verified",
        successTitleDefault: "Security Clearance request verified!",
      });
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      setVerifyDialogOpen(false);
      notify({
        title: t("common.error"),
        description:
          error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const isBulkActionInProgress =
    bulkClaimMutation.isPending ||
    bulkUnclaimMutation.isPending ||
    bulkUnsuspendMutation.isPending ||
    bulkCancelMutation.isPending ||
    bulkVerifyMutation.isPending;

  const drawerSelectedRows = selectedRows.map((r) => ({
    id: r.id,
    serialNo: r.serialNo,
    taskId: r.taskId ?? null,
  }));

  return (
    <PageLayout dir={dir}>
      <LoadingOverlay loading={isLoading || (isFetching && !isBulkActionInProgress)} />
      <Subheader
        testId="clearances-page-subheader"
        subtitle={t("security-clearance.security-clearance")}
        title={
          <div className="flex items-end justify-between w-full">
            <span>{t("security-clearance.security-clearance")}</span>
            {checkPermission(userActionPermissions, "canRequestSecurityClearance") && (
              <Button
                type="button"
                variant="contained"
                color="primary"
                testId="clearances-page-create-button"
                label={
                  <span className="text-sm font-medium flex items-center gap-2">
                    <AddCircleOutlineIcon fontSize="small" />
                    {t(requestSCLabelKey)}
                  </span>
                }
                sx={{ textTransform: "none", height: "40px", minHeight: "40px" }}
                onClick={() => navigate("/port-security/request-security-clearance/check-id")}
              />
            )}
          </div>
        }
      />
      <div className="mt-8 px-24">
        <CustomDataTable
          hasCheckboxes={canUseBulkActions}
          title={t("security-clearance.clearances-title")}
          actionName={t(requestSCLabelKey)}
          testId="security-clearance-table"
          headers={headers}
          rows={formattedFormData}
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          canPreview={
            !isBulkActionInProgress &&
            checkPermission(userActionPermissions, "canPreviewSecurityClearanceRequest")
          }
          onPageChange={(newPage, newSize) => {
            if (newPage !== page || newSize !== pageSize) {
              startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
            }
            setPage(newPage);
            setPageSize(newSize);
            localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, String(newPage));
            localStorage.setItem(LOCAL_STORAGE_PAGE_SIZE_KEY, String(newSize));
          }}
          navigationLinkBase="/port-security/security-clearance-request-preview"
          filters={filters}
          onApplyFilters={onApplyFilters}
          onClearFilters={onClearFilters}
          availableFields={availableFields}
          isRowSelectable={isRowSelectable}
          onSelectionChange={handleSelectionChange}
          rowSelectionModel={rowSelectionModel}
          isLoading={isLoading}
          headerContent={
            selectedRows.length > 0 && canUseBulkActions ? (
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium">
                  {t("security-clearance.actions-label", { defaultValue: "Actions" })}:
                </span>
                {selectedOnlyUnclaimed && (
                  <Button
                    type="button"
                    startIcon={<ContentPasteGoIcon />}
                    variant="contained"
                    color="primary"
                    testId="security-clearance-bulk-claim"
                    label={
                      <span className="text-sm font-medium normal-case">
                        {t("security-clearance.claim", { defaultValue: "Claim" })}
                      </span>
                    }
                    onClick={() => {
                      const taskIds = getSelectedTaskIds();
                      if (taskIds.length > SECURITY_CLEARANCE_BULK_MAX) {
                        notifyBulkLimitExceeded();
                        return;
                      }
                      if (taskIds.length) {
                        bulkClaimMutation.mutate(taskIds);
                      }
                    }}
                    disabled={isBulkActionInProgress}
                  />
                )}
                {selectedOnlyClaimedByCurrentUser && (
                  <>
                    <Button
                      type="button"
                      variant="outlined"
                      color="primary"
                      testId="security-clearance-bulk-unclaim"
                      label={
                        <span className="text-sm font-medium normal-case">
                          {t("security-clearance.unclaim", { defaultValue: "Unclaim" })}
                        </span>
                      }
                      onClick={() => {
                        const taskIds = getSelectedTaskIds();
                        if (taskIds.length > SECURITY_CLEARANCE_BULK_MAX) {
                          notifyBulkLimitExceeded();
                          return;
                        }

                        setUnclaimDialogOpen(true);
                      }}
                      disabled={isBulkActionInProgress}
                    />
                    <Button
                      type="button"
                      variant="contained"
                      startIcon={<ContentPasteGoIcon />}
                      color="primary"
                      testId="security-clearance-bulk-approve"
                      label={
                        <span className="text-sm font-medium normal-case">
                          {shouldDirectVerify
                            ? t("security-clearance.verify", { defaultValue: "Verified" })
                            : t("security-clearance.approve", {
                                defaultValue: "Approve",
                              })}
                        </span>
                      }
                      onClick={() => {
                        if (exceedsBulkLimit) {
                          notifyBulkLimitExceeded();
                          return;
                        }
                        if (shouldDirectVerify) {
                          setVerifyDialogOpen(true);
                          return;
                        }
                        setBulkApproveDrawerOpen(true);
                      }}
                      disabled={isBulkActionInProgress}
                    />
                    <Button
                      type="button"
                      variant="outlined"
                      color="primary"
                      startIcon={<SyncIcon />}
                      testId="security-clearance-bulk-resubmit"
                      label={
                        <span className="text-sm font-medium normal-case">
                          {t("security-clearance.resubmit", {
                            defaultValue: "Resubmit",
                          })}
                        </span>
                      }
                      onClick={() => {
                        if (exceedsBulkLimit) {
                          notifyBulkLimitExceeded();
                          return;
                        }
                        setBulkResubmitDrawerOpen(true);
                      }}
                      disabled={isBulkActionInProgress}
                    />
                    {canBulkPermanentlyReject && (
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        startIcon={<HighlightOffIcon />}
                        testId="security-clearance-bulk-permanently-reject"
                        label={
                          <span className="text-sm font-medium normal-case">
                            {t("security-clearance.permanently-reject", {
                              defaultValue: "Permanently reject",
                            })}
                          </span>
                        }
                        onClick={() => {
                          if (exceedsBulkLimit) {
                            notifyBulkLimitExceeded();
                            return;
                          }
                          setBulkRejectDrawerOpen(true);
                        }}
                        disabled={isBulkActionInProgress}
                      />
                    )}
                  </>
                )}
                {selectedAllApproved && (canSuspend || canCancel) && (
                  <>
                    {canSuspend && (
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        startIcon={<DoDisturbIcon />}
                        testId="security-clearance-bulk-suspend"
                        label={
                          <span className="text-sm font-medium normal-case">
                            {t("security-clearance.suspend", {
                              defaultValue: "Suspend",
                            })}
                          </span>
                        }
                        onClick={() => {
                          if (exceedsBulkLimit) {
                            notifyBulkLimitExceeded();
                            return;
                          }
                          setBulkSuspendDrawerOpen(true);
                        }}
                        disabled={isBulkActionInProgress}
                      />
                    )}
                    {canCancel && (
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        startIcon={<HighlightOffIcon />}
                        testId="security-clearance-bulk-cancel"
                        label={
                          <span className="text-sm font-medium normal-case">
                            {t("security-clearance.cancel", {
                              defaultValue: "Cancel",
                            })}
                          </span>
                        }
                        onClick={() => {
                          if (exceedsBulkLimit) {
                            notifyBulkLimitExceeded();
                            return;
                          }
                          setCancelDialogOpen(true);
                        }}
                        disabled={isBulkActionInProgress}
                      />
                    )}
                  </>
                )}
                {selectedAllSuspended && canUnsuspend && (
                  <Button
                    type="button"
                    variant="outlined"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    testId="security-clearance-bulk-unsuspend"
                    label={
                      <span className="text-sm font-medium normal-case">
                        {t("security-clearance.unsuspend", {
                          defaultValue: "Unsuspend",
                        })}
                      </span>
                    }
                    onClick={() => {
                      if (exceedsBulkLimit) {
                        notifyBulkLimitExceeded();
                        return;
                      }
                      setUnsuspendDialogOpen(true);
                    }}
                    disabled={isBulkActionInProgress}
                  />
                )}
              </div>
            ) : null
          }
        />
      </div>
      {isBulkActionInProgress && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-white/40">
          <CircularProgress />
        </div>
      )}
      <BulkApproveSecurityClearanceDrawer
        isOpen={bulkApproveDrawerOpen}
        onClose={() => setBulkApproveDrawerOpen(false)}
        requireValidityPeriod={requireValidityPeriod}
        validityPeriodOptions={bulkValidityPeriodOptions}
        selectedRows={drawerSelectedRows}
        onSuccess={() => {
          refreshClearancesListAfterMutation();
          clearSelectionAfterBulkComplete();
        }}
      />
      <BulkReasonSecurityClearanceDrawer
        mode="resubmit"
        isOpen={bulkResubmitDrawerOpen}
        onClose={() => setBulkResubmitDrawerOpen(false)}
        selectedRows={drawerSelectedRows}
        onSuccess={() => {
          refreshClearancesListAfterMutation();
          clearSelectionAfterBulkComplete();
        }}
      />
      <BulkReasonSecurityClearanceDrawer
        mode="permanentReject"
        isOpen={bulkRejectDrawerOpen}
        onClose={() => setBulkRejectDrawerOpen(false)}
        selectedRows={drawerSelectedRows}
        onSuccess={() => {
          refreshClearancesListAfterMutation();
          clearSelectionAfterBulkComplete();
        }}
      />
      <BulkReasonSecurityClearanceDrawer
        mode="suspend"
        isOpen={bulkSuspendDrawerOpen}
        onClose={() => setBulkSuspendDrawerOpen(false)}
        selectedRows={drawerSelectedRows}
        onSuccess={() => {
          refreshClearancesListAfterMutation();
          clearSelectionAfterBulkComplete();
        }}
      />
      <GenericDialog
        open={verifyDialogOpen}
        title={t("security-clearance.bulk-verify-confirm-title", {
          defaultValue: "Confirm bulk verify",
        })}
        elementContent={
          <>
            {t("security-clearance.bulk-verify-confirm-message", {
              defaultValue: "You are about to verify {{count}} security clearance request(s).",
              count: selectedRows.length,
            })}
            <br />
            {t("security-clearance.bulk-confirm-question", {
              defaultValue: "Are you sure you want to proceed?",
            })}
          </>
        }
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t("common.confirm", {
          defaultValue: "Confirm",
        })}
        handleCloseDialog={() => setVerifyDialogOpen(false)}
        confirmAction={() => {
          if (exceedsBulkLimit) {
            notifyBulkLimitExceeded();
            return;
          }
          const ids = getSelectedClearanceIds();
          if (ids.length) bulkVerifyMutation.mutate(ids);
        }}
        testId="security-clearance-bulk-verify-confirm-dialog"
      />
      <GenericDialog
        open={unsuspendDialogOpen}
        title={t("security-clearance.bulk-unsuspend-confirm-title", {
          defaultValue: "Confirm bulk unsuspend",
        })}
        elementContent={
          <>
            {t("security-clearance.bulk-unsuspend-confirm-message", {
              defaultValue: "You are about to unsuspend {{count}} security clearance request(s).",
              count: selectedRows.length,
            })}
            <br />
            {t("security-clearance.bulk-confirm-question", {
              defaultValue: "Are you sure you want to proceed?",
            })}
          </>
        }
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t("common.confirm", {
          defaultValue: "Confirm",
        })}
        handleCloseDialog={() => setUnsuspendDialogOpen(false)}
        confirmAction={() => {
          if (exceedsBulkLimit) {
            notifyBulkLimitExceeded();
            return;
          }
          const ids = getSelectedClearanceIds();
          if (ids.length) bulkUnsuspendMutation.mutate(ids);
        }}
        testId="security-clearance-bulk-unsuspend-confirm-dialog"
      />
      <GenericDialog
        open={cancelDialogOpen}
        title={t("security-clearance.bulk-cancel-confirm-title", {
          defaultValue: "Confirm bulk cancel",
        })}
        elementContent={
          <>
            {t("security-clearance.bulk-cancel-confirm-message", {
              defaultValue: "You are about to cancel {{count}} security clearance request(s).",
              count: selectedRows.length,
            })}
            <br />
            {t("security-clearance.bulk-confirm-question", {
              defaultValue: "Are you sure you want to proceed?",
            })}
          </>
        }
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t("common.confirm", {
          defaultValue: "Confirm",
        })}
        handleCloseDialog={() => setCancelDialogOpen(false)}
        confirmAction={() => {
          if (exceedsBulkLimit) {
            notifyBulkLimitExceeded();
            return;
          }
          const ids = getSelectedClearanceIds();
          if (ids.length) bulkCancelMutation.mutate(ids);
        }}
        testId="security-clearance-bulk-cancel-confirm-dialog"
      />
      <ConfirmUnclaimDialog
        isOpen={unclaimDialogOpen}
        setIsOpen={setUnclaimDialogOpen}
        confirmAction={() => {
          const taskIds = getSelectedTaskIds();

          if (taskIds.length) {
            bulkUnclaimMutation.mutate(taskIds);
          }
        }}
        count={selectedRows.length}
        languageBase="security-clearance"
        testId="security-clearance-bulk-unclaim-confirm-dialog"
        isBulk
      />
    </PageLayout>
  );
};

export default ClearancesContainer;
